import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Wordmark, FoxIcon } from '../components/Brand';
import { TryFreeLink } from '../components/TryFreeLink';
import { Sparkle } from '../components/Sparkle';
import { HeroBanner } from '../components/home/HeroBanner';
import { DiscoverNext } from '../components/home/DiscoverNext';
import { FeatureCards } from '../components/home/FeatureCards';
import { HomeTiles } from '../components/home/HomeTiles';
import { APP_STATUS } from '../lib/links';

/** Very subtle halftone dots across the lavender page. */
const DOTS = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3E%3Ccircle cx='2' cy='2' r='1.3' fill='%231E1B39' fill-opacity='0.07'/%3E%3C/svg%3E\")";
const BOLT_PATH = 'M58 2L14 58H40L30 98L86 36H58L70 2Z';
export function Home() {
  const reduceMotion = useReducedMotion();
  const rise = (delay: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1] as const,
      delay
    }
  };
  return <div className="relative isolate overflow-x-clip bg-lavender pb-16 md:pb-24" data-id="element-355" data-mp="2vyhss2iroqsc">
      {/*
        The logo area stays plain #D8D1FC so the wordmark artwork blends in.
        Halftone dots and the soft glow only begin below it.
       */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 top-[260px] -z-10 sm:top-[300px] lg:top-[320px]" style={{
      backgroundImage: DOTS
    }} data-id="element-356" data-mp="2hxnkrkzmsru7" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-[520px] -z-10 h-[620px] w-[1100px] max-w-[160vw] -translate-x-1/2 rounded-full bg-white/40 blur-3xl" data-id="element-357" data-mp="287ksjn0c3hwx" />

      {/* A few quiet accents at the outer edges, wide screens only */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 hidden xl:block" data-id="element-358" data-mp="1u8cpgj11vpng">
        <svg viewBox="0 0 100 100" className="absolute left-[2%] top-[420px] h-44 w-44 -rotate-12" data-id="element-359" data-mp="1kkqnlgkg8gf0">
          <path d={BOLT_PATH} fill="#E6E0FD" data-id="element-360" data-mp="1omc4dd03teuh" />
        </svg>
        <svg viewBox="0 0 100 100" className="absolute right-[2%] top-[980px] h-40 w-40 rotate-12" data-id="element-361" data-mp="1onvl14po9rws">
          <path d={BOLT_PATH} fill="#E6E0FD" data-id="element-362" data-mp="1aoduajstg4ks" />
        </svg>
      </div>
      <Sparkle className="absolute left-[7%] top-24 hidden h-6 w-6 lg:block" drift={9} data-id="element-363" data-mp="1fmp4wwqxqj42" />
      <Sparkle className="absolute right-[8%] top-40 hidden h-5 w-5 lg:block" drift={10} delay={1.6} data-id="element-364" data-mp="23ayk3ftn88m8" />
      <Sparkle className="absolute right-[4%] top-[760px] hidden h-4 w-4 xl:block" drift={11} delay={3} data-id="element-365" data-mp="28akznvwy20y7" />

      <div className="mx-auto max-w-[1080px] px-2 sm:px-5" data-id="element-366" data-mp="1tkdrqa4kc0br">
        {/* On home the header floats over the page and holds only corner links,
            so the wordmark sits in normal flow at the top, never clipped. */}
        <motion.div {...rise(0)} className="flex justify-center pt-11 sm:pt-10 lg:pt-8" data-id="element-367" data-mp="2m8i053eiqri5">
          <Wordmark eager className="h-[144px] w-[216px] sm:h-[160px] sm:w-[240px] lg:h-[170px] lg:w-[255px]" data-id="element-368" data-mp="2r1kql2o01ku4" />
        </motion.div>

        <div className="relative -mt-3 rounded-[32px] bg-cream px-3 pb-8 pt-8 shadow-[0_40px_80px_-40px_rgba(30,27,57,0.45)] sm:rounded-t-[72px] sm:px-8 sm:pb-10 md:rounded-t-[120px] md:px-12 md:pt-10" data-id="element-369" data-mp="an9214tf1mtw">
          <div className="mx-auto max-w-2xl text-center" data-id="element-370" data-mp="kcafan8ktq46">
            <motion.p {...rise(0.04)} className="text-[13px] font-extrabold uppercase tracking-[0.14em] text-ink sm:text-[14px]" data-id="element-371" data-mp="kewm2knn81lc">
              A learning app for curious kids
            </motion.p>
            <motion.h1 {...rise(0.06)} className="mt-3 font-display text-[32px] leading-[1.05] tracking-tight text-ink [text-wrap:balance] min-[360px]:text-[36px] sm:text-[48px] lg:text-[56px]" data-id="element-372" data-mp="5nwesiymudqs">
              <span className="block" data-id="element-373" data-mp="1h0yjogrddev4">Big questions.</span>
              <span className="block" data-id="element-374" data-mp="1c4hwborzv4j3">Brilliant discoveries.</span>
            </motion.h1>
            <motion.p {...rise(0.1)} className="mx-auto mt-4 max-w-xl text-[17px] text-ink/85 sm:text-[19px]" data-id="element-375" data-mp="27eeq0aobh1hx">
              Give your child a break from “brain rot” videos and something to get curious about. With Lightning
              Learn, they read or listen to illustrated lessons, answer questions, and earn badges as they explore
              science, animals, space, history, and more.
            </motion.p>
            <motion.div {...rise(0.14)} className="mt-6 flex flex-col items-center" data-id="element-376" data-mp="2qbpovu61dswu">
              <TryFreeLink data-id="element-377" data-mp="2vbc4295fdj8a" />
            </motion.div>
            <motion.p {...rise(0.18)} className="mt-3 inline-flex items-center gap-2 text-[14px] font-semibold text-ink/70" data-id="element-378" data-mp="2v9r6sbrq7m6q">
              <FoxIcon className="h-6 w-6" data-id="element-379" data-mp="350c29qc3salf" />
              {APP_STATUS}
            </motion.p>
          </div>

          <HeroBanner data-id="element-380" data-mp="3a2b611br99iw" />
          <DiscoverNext data-id="element-381" data-mp="39yneg7pvex6t" />
          <FeatureCards data-id="element-382" data-mp="3e223fnvsenwe" />
          <HomeTiles data-id="element-383" data-mp="3e3d7zpx23zok" />
        </div>
      </div>
    </div>;
}